import BaseDesignDataService from "@configuratorware/configurator-frontendgui/App/Services/DesignDataService";
import { Services } from "@configuratorware/configurator-frontendgui/App/ServiceLocator";
import isEqual from "lodash/isEqual";
import FontFaceObserver from "fontfaceobserver";

import fonts from "../Resources/Fonts";
import { HAS_NAME_IDS, HAS_PHONE_IDS } from "../Custom/App/Constants";

import { getCustomTextDisplayType } from "../Helpers";

const phoneNumberParams = {
  x: -40,
  y: 6,
  fontSize: 18,
  fontFamily: "Gerade",
  align: "right",
};

const nameParams = {
  x: 40,
  y: 6,
  fontSize: phoneNumberParams.fontSize * 1.35,
  align: "left",
};


function loadFont({ name, formats }) {
  const style = document.createElement("style");
  style.appendChild(
    document.createTextNode(`
  @font-face {
    font-family: "${name}";
    src: ${formats
      .map(({ format, url }) => `url("${url}") format("${format}")`)
      .join(", ")};
  }
  `)
  );
  document.head.appendChild(style);
  return new FontFaceObserver(name).load(null, 60000);
}

function getVisualizationParams(state) {
  const selectedDisplay = getCustomTextDisplayType(state);

  const {
    displayName = "",
    displayPhone = "",
    textFontFamily,
    textColor,
  } = state.configurator?.configuration?.customdata || {};

  return {
    color: textColor?.value || "#FFFFFF",
    name: HAS_NAME_IDS.includes(selectedDisplay) ? displayName || "" : "",
    phoneNumber: HAS_PHONE_IDS.includes(selectedDisplay)
      ? displayPhone || ""
      : "",
    fontFamily: textFontFamily || "Holli",
  };
}

class DesignDataService extends BaseDesignDataService {
  init() {
    super.init();
    Services.store.observeStore(
      getVisualizationParams,
      async (visualizationParams) => {
        if (!isEqual(this._params, visualizationParams)) {
          this._params = visualizationParams;
          await this.updateVisualization();
        }
      }
    );
    this.fontsLoaded = this.loadFonts();
  }

  async loadFonts() {
    try {
      await Promise.all(fonts.map(loadFont));
      console.info("Fonts are loaded successfully.");
    } catch (e) {
      console.error("Could not load fonts:", e);
      throw e;
    }
  }

  drawTexts = async (getDrawingContext) => {
    const drawingFn = async (textureCtx, { width, height, ratio }) => {
      try {
        await this.fontsLoaded;

        const { color, name, phoneNumber, fontFamily } = this._params;

        textureCtx.save();

        textureCtx.translate(width * 0.5, height * 0.5);
        textureCtx.scale(ratio, ratio);
        textureCtx.fillStyle = color;

        // draw name
        textureCtx.font = `${
          fontFamily === "Holli"
            ? nameParams.fontSize * 1.3
            : nameParams.fontSize
        }px ${fontFamily}`;
        textureCtx.textAlign = nameParams.align;
        textureCtx.fillText(name, nameParams.x, nameParams.y);

        // draw phone number
        textureCtx.font = `${phoneNumberParams.fontSize}px ${phoneNumberParams.fontFamily}`;
        textureCtx.textAlign = phoneNumberParams.align;
        textureCtx.fillText(
          phoneNumber,
          phoneNumberParams.x,
          phoneNumberParams.y
        );

        textureCtx.restore();
      } catch (e) {
        console.error(e);
      }
    };
    try {
      await getDrawingContext(
        drawingFn,
        true /* update alpha */,
        true /* clean up texture before drawing */
      );
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn(
        "Could not get drawing context, texture update is skipped!",
        e.message
      );
    }
  };

  async updateVisualization(updateEventObject = {}) {
    if (!Services.visualization.isReadyToRender()) {
      return;
    }

    Services.ui.show("loadVisualization");
    try {
      await Services.visualization.updateTexture({
        updateEventObject,
        drawingFunction: this.drawTexts,
      });
    } finally {
      Services.ui.hide("loadVisualization");
    }
  }
}

export default DesignDataService;
